// Tracks fix cards the user has hidden ("this fix doesn't apply"). Hidden cards
// stay visible (dimmed) in the app so they can be un-hidden, but are stripped
// from every export (full-audit PDF and per-tab PDF/HTML).
//
// Persisted in localStorage so the choice survives refreshes. Keyed by the same
// fixId used by the state engine (the ".ac" id minus the "ac-" prefix).

const HIDDEN_KEY = "seo_hidden_fixes";

export type HiddenMap = Record<string, boolean>;

export function loadHidden(): HiddenMap {
  try {
    const v = JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? "{}");
    return v && typeof v === "object" ? v as HiddenMap : {};
  } catch { return {}; }
}

export function saveHidden(h: HiddenMap): void {
  try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(h)); } catch { /* ignore */ }
}

export function toggleHidden(h: HiddenMap, id: string): HiddenMap {
  const next = { ...h };
  if (next[id]) delete next[id]; else next[id] = true;
  return next;
}

// The visible "card" that wraps a fix's .ac control strip. Cards are heterogeneous
// across sections — table rows in some tabs, distinct card divs in others — so we
// match the known wrappers and fall back to the .ac's parent.
const CARD_SELECTOR = ".oc, tr, .gap-card, .il-c, .um, .faq-item";

export function cardOf(ac: Element): HTMLElement {
  return (ac.closest(CARD_SELECTOR) as HTMLElement | null)
    ?? (ac.parentElement as HTMLElement | null)
    ?? (ac as HTMLElement);
}

// Removes every hidden fix's card from a parsed section/document (used by exports).
export function stripHiddenCards(root: ParentNode, hidden: HiddenMap): void {
  for (const id of Object.keys(hidden)) {
    if (!hidden[id]) continue;
    // Attribute selector avoids needing CSS.escape for ids that contain hyphens/slugs.
    const ac = root.querySelector(`[id="ac-${id}"]`);
    if (ac) cardOf(ac).remove();
  }
}
